import { forEach, isEqual } from 'lodash'
import {
    RefObject,
    MouseEvent,
    createRef,
    PureComponent,
    CSSProperties,
} from 'react'
import {
    ViewerClass,
    MarkupManager,
    DfmFeatures,
    CrossSectionViewType,
    DfmFeatureType,
} from 'fictiv-3d-viewer'
import { Spin, Alert } from 'antd'
import sizeMe from 'react-sizeme'
import { Vector3, Texture, Matrix4 } from 'three'
import { TripleFloatType } from 'fictiv-3d-viewer/src/lib/types'
import { P2pPoint } from 'fictiv-3d-viewer/dist/lib/PointToPointMeasurementTool'

import logger from '~/lib/logger'
import Box from '~/core/Box'
import Image from '~/core/Image'
import Link from '~/core/Link'
import Text from '~/core/Text'
import { translateCameraPositionalData } from '~/redux-modules/qmr-viewer/utils'
import icon from '../../lib/getPlaceholderImage/icon.svg'
import {
    CROSS_SECTION_ANNOTATION_MODE,
    POINT_TO_POINT_MODE,
} from '~/redux-modules/qmr-viewer/constants'
import { Matrix4FloatType } from '~/redux-modules/qmr-viewer/types'

import pinImgData from './assets/map-marker.png'
import pointImgData from './assets/disc.png'
import { SUCCESSFULLY_INITIALIZED, FAILED_TO_INITIALIZE } from './logic'
import styles from './styles.css'

type CrossSectionConfig = {
    enabled: boolean
    view?: CrossSectionViewType
    hoveredTool: string
    toolsHidden: boolean
    crossSectionRotation: Matrix4
    crossSectionPosition: number[]
    mode: string
}

type CameraConfig = {
    zoom: number
    position: number[]
    target: number[]
    up: number[]
    pivot: number[]
}

const TORUS_X = 'torusX'
const TORUS_Y = 'torusY'
const ARROW = 'arrow'
const PLACEMENT = 'model-viewer'
let pinTexture: Texture | null = null
let pointTexture: Texture | null = null

/* Initialize pin texture */
MarkupManager.getTextureFromImageURL(pinImgData)
    .then(texture => {
        pinTexture = texture
        return texture
    })
    .catch(e => {
        logger.error(e)
        return null
    })

MarkupManager.getTextureFromImageURL(pointImgData)
    .then(texture => {
        pointTexture = texture
        return texture
    })
    .catch(e => {
        logger.error(e)
        return null
    })

const HOVERED_CROSS_SECTION_TOOL_COLOR = 'cyan'

const UNINITIALIZED = 'uninitialized'
const INITIALIZING = 'initializing'

type Status =
    | typeof INITIALIZING
    | typeof UNINITIALIZED
    | typeof SUCCESSFULLY_INITIALIZED
    | typeof FAILED_TO_INITIALIZE
type getViewerStatusType = (status: Status) => void

export interface ModelViewerProps {
    getViewerStatus?: getViewerStatusType
    sceneBundle?: Record<string, any>
    onUnmount?: () => void
    cameraConfig?: CameraConfig
    onViewerInstantiate?: (viewer: ViewerClass) => void
    isResetView?: boolean
    setResetView?: (isResetView: boolean) => void
    size?: any
    patchConfigs?: Record<string, any>
    annotationConfigs?: Array<Parameters<MarkupManager['createAnnotation']>[0]>
    transparent?: boolean
    dfmFeatureConfigs?: { [key in DfmFeatureType]: any }
    crossSectionConfig?: CrossSectionConfig
    interactionMode?: string
    partRotation?: Matrix4FloatType
    customP2PPoints?: TripleFloatType[]
    selectedPoints?: P2pPoint[]
    hoveredPoint?: P2pPoint
    onPatchClick?: (patchClickEvent: any) => unknown
    onPatchCtrlClick?: (patchClickEvent: any) => unknown
    onPatchRightClick?: (patchClickEvent: any) => unknown
    onPatchHover?: (args: { patchHash: any }) => unknown
    onCameraPan?: () => unknown
    onCameraZoom?: () => unknown
    onCameraRotation?: () => unknown
    onAnnotationClick?: (args: { annotation: any }) => unknown
    onCrossSectionToolClick?: (args: {
        normal: Vector3
        pointCoordinates: Vector3
    }) => void
    onBackgroundClick?: () => unknown
    onEscKeyUp?: () => void
    onCrossSectionToolHover?: (args: { tool: any }) => unknown
    onViewCubeClick?: () => unknown
    onPointHover?: (point: any) => unknown
    onPointLeftClick?: (args: { clickIndex: any; clickType: any }) => unknown
    onPointRightClick?: () => unknown
    onMultiplePatchHover?: (args: { patchHashes: any }) => unknown
    onMultiplePatchClick?: (args: { patchHashes: any }) => unknown
    isConfigurationModal?: boolean
    openChat?: () => void
}

interface State {
    viewerContainerRef: any
    viewer: any
}

class ModelViewer extends PureComponent<ModelViewerProps, State> {
    // We set this as an object property instead of as internal state
    // This is because some clients load successfully on mount,
    // and others need to run through an update cycle
    // A problem was arising, because of a race condition between the setState
    // and the render, causing the viewer to sometimes be instantiated twice,
    // which broke the interactions
    setLoadingState = (status: Status) => {
        return this.props.getViewerStatus && this.props.getViewerStatus(status)
    }
    viewerContainerRef: RefObject<HTMLDivElement>
    viewer: ViewerClass | null
    loadSuccess: Status
    constructor(props: ModelViewerProps) {
        super(props)
        this.viewerContainerRef = createRef()
        this.viewer = null
        this.loadSuccess = UNINITIALIZED
        this.setLoadingState(this.loadSuccess)
    }

    componentDidMount() {
        if (
            this.props.sceneBundle &&
            this.viewer == null &&
            this.loadSuccess === UNINITIALIZED
        ) {
            this.loadSuccess = INITIALIZING
            this.initViewer()
            this.setLoadingState(this.loadSuccess)
        }
    }

    componentWillUnmount() {
        if (this.props.onUnmount) {
            this.props.onUnmount()
        }
    }

    componentDidUpdate(prevProps: ModelViewerProps) {
        if (
            this.viewer == null &&
            this.props.sceneBundle &&
            this.loadSuccess === UNINITIALIZED
        ) {
            this.loadSuccess = INITIALIZING
            this.initViewer()
        }

        this.updateViewer(prevProps)
    }

    initViewer() {
        if (!this.viewerContainerRef.current || !this.props?.sceneBundle) {
            return
        }

        return ViewerClass.createFromPartSceneBundle(
            this.viewerContainerRef.current,
            this.props.sceneBundle,
        )
            .then(viewer => {
                if (!viewer) {
                    return
                }

                this.viewer = viewer
                const {
                    zoom,
                    positionArray,
                    targetArray,
                    upArray,
                    pivotArray,
                } = viewer.getDefaultCameraProps()

                const { cameraConfig } = this.props

                viewer.setCameraZoom({ zoom: cameraConfig?.zoom || zoom })
                viewer.setCameraPositionFromArray({
                    positionArray: cameraConfig?.position || positionArray,
                })
                viewer.setCameraTargetFromArray({
                    targetArray: cameraConfig?.target || targetArray,
                })
                viewer.setCameraUpFromArray({
                    upArray: cameraConfig?.up || upArray,
                })
                viewer.setCameraPivotFromArray({
                    pivotArray: cameraConfig?.pivot || pivotArray,
                })

                viewer.InteractionController.updateTrackballControls()

                if (this.props.onViewerInstantiate) {
                    this.props.onViewerInstantiate(viewer)
                }

                if (pointTexture) {
                    viewer.InteractionController.setPointToPointMeasurementTexture(
                        pointTexture,
                    )
                }

                this.loadSuccess = SUCCESSFULLY_INITIALIZED
                this.setLoadingState(this.loadSuccess)
                // We need to cause force another render loop here
                // because loadSuccess is an object property instead of state
                this.setState({})
                if (this.props.crossSectionConfig) {
                    this.updateCrossSection(
                        viewer,
                        this.props.crossSectionConfig,
                        this.props.crossSectionConfig,
                        true,
                    )
                }
                return viewer
            })
            .catch(err => {
                logger.error(err)

                this.setState({})
                this.loadSuccess = FAILED_TO_INITIALIZE
                this.setLoadingState(this.loadSuccess)
                return false
            })
    }

    updateViewer(prevProps: ModelViewerProps) {
        const { viewer } = this
        const { isResetView, setResetView } = this.props

        if (!viewer) {
            return
        }

        /* Check if we need to update viewer size */
        if (
            prevProps.size &&
            this.props.size.width &&
            this.props.size.height &&
            (prevProps.size.width !== this.props.size.width ||
                prevProps.size.height !== this.props.size.height)
        ) {
            viewer.resizeCanvasAndRenderer()
        }

        if (this.props.dfmFeatureConfigs !== prevProps.dfmFeatureConfigs) {
            if (prevProps.dfmFeatureConfigs) {
                const prevDfmFeatureConfigsKeys = Object.keys(
                    prevProps.dfmFeatureConfigs,
                ) as DfmFeatureType[]

                prevDfmFeatureConfigsKeys.forEach(feature => {
                    /* Turn off feature if no longer in config */
                    /* Could use a 'reset feature' api to reset configs to default */
                    if (this.props.dfmFeatureConfigs?.[feature] == null) {
                        viewer.DfmFeedback.setFeatureVisibility({
                            feature,
                            visible: false,
                        })
                    }
                })
            }

            if (this.props.dfmFeatureConfigs) {
                const currentDfmFeatureConfigsEntries = Object.entries(
                    this.props.dfmFeatureConfigs,
                ) as [DfmFeatureType, any][]

                currentDfmFeatureConfigsEntries.forEach(([feature, config]) => {
                    /* Turn on added features */
                    /* Could use a 'setFeatureConfig' api to set subset of all fields all at once */
                    viewer.DfmFeedback.setFeatureVisibility({
                        feature,
                        visible: true,
                    })
                    if (config['threshold'] != null) {
                        viewer.DfmFeedback.setFeatureThreshold({
                            feature,
                            threshold: config.threshold,
                        })
                    }

                    if (feature === DfmFeatures.DRAFT_ANALYSIS) {
                        viewer.DfmFeedback.setFeatureDirection({
                            feature,
                            direction: config.direction,
                        })
                    }
                })
            }
        }

        if (this.props.patchConfigs !== prevProps.patchConfigs) {
            if (prevProps.patchConfigs) {
                Object.entries(prevProps.patchConfigs).forEach(
                    ([patchHash, config]) => {
                        if (this.props.patchConfigs?.[patchHash] == null) {
                            const part = viewer.getPart()

                            part.setPatchColorByHash({
                                patchHashes: [patchHash],
                                color: 'gainsboro' /* should use a default const */,
                            })

                            if (config.highlight) {
                                part.setPatchMaterialHighlightByHash({
                                    patchHashes: [patchHash],
                                    highlight: false,
                                })
                            }
                        }
                    },
                )
            }

            if (this.props.patchConfigs) {
                Object.entries(this.props.patchConfigs).forEach(
                    ([patchHash, config]) => {
                        const part = viewer.getPart()

                        part.setPatchColorByHash({
                            patchHashes: [patchHash],
                            color:
                                config.color ||
                                'gainsboro' /* should use a default const */,
                        })

                        if (config.highlight) {
                            part.setPatchMaterialHighlightByHash({
                                patchHashes: [patchHash],
                                highlight: true,
                            })
                        }
                    },
                )
            }
        }

        if (
            prevProps.annotationConfigs?.length ||
            this.props.annotationConfigs !== prevProps.annotationConfigs
        ) {
            /* Clear existing annotations */
            viewer.MarkupManager.deleteAnnotationsById({
                annotationIds: [],
            })
            /* Add current annotations */
            // todo: use Object.values instead lodash forEach
            forEach(this.props.annotationConfigs, ({ position, metadata }) => {
                if (!pinTexture) {
                    return
                }

                // todo: add rotation argument, after adding proper types it seems this call missing required argument "rotation"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                viewer.MarkupManager.createAnnotation({
                    sprite: pinTexture,
                    metadata,
                    position, //Intersection point in local coordinates
                })
            })
        }

        if (this.props.transparent !== prevProps.transparent) {
            viewer.getPart().setTransparency({
                transparent: this.props.transparent,
                opacity: 0.5,
            })
        }

        if (this.props.crossSectionConfig) {
            this.updateCrossSection(
                viewer,
                this.props.crossSectionConfig,
                prevProps.crossSectionConfig,
                false,
            )
        }

        const { partRotation, cameraConfig } = this.props

        if (partRotation) {
            if (
                cameraConfig &&
                cameraConfig.zoom &&
                cameraConfig.position &&
                !isEqual(partRotation, prevProps.partRotation)
            ) {
                //Backwards compatibility with old CQ viewer data
                const { position, target, up } = translateCameraPositionalData({
                    newViewerCameraPosition: cameraConfig.position,
                    newViewerPartMatrixArray: partRotation,
                })

                viewer.setCameraZoom({ zoom: cameraConfig.zoom })
                viewer.setCameraPositionFromArray({ positionArray: position })
                viewer.setCameraTargetFromArray({ targetArray: target })
                viewer.setCameraUpFromArray({ upArray: up })
                viewer.setCameraPivotFromArray({ pivotArray: [0, 0, 0] })
                viewer.InteractionController.updateTrackballControls()
            }
        } else {
            let cameraSet = false

            if (
                (cameraConfig?.zoom &&
                    !isEqual(
                        cameraConfig?.zoom,
                        prevProps.cameraConfig?.zoom,
                    )) ||
                isResetView
            ) {
                // according to props camera config is optional, but setCameraZoom requires some number
                // todo: fix and provide some default value if cameraConfig prop isn't specified
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                viewer.setCameraZoom({ zoom: cameraConfig?.zoom })
                cameraSet = true

                if (setResetView) {
                    setResetView(false)
                }
            }
            if (
                (cameraConfig?.position &&
                    !isEqual(
                        cameraConfig?.position,
                        prevProps.cameraConfig?.position,
                    )) ||
                isResetView
            ) {
                viewer.setCameraPositionFromArray({
                    positionArray: cameraConfig?.position || [],
                })
                cameraSet = true

                if (setResetView) {
                    setResetView(false)
                }
            }
            if (
                (cameraConfig?.up &&
                    !isEqual(cameraConfig?.up, prevProps.cameraConfig?.up)) ||
                isResetView
            ) {
                viewer.setCameraUpFromArray({
                    upArray: cameraConfig?.up || [],
                })
                cameraSet = true

                if (setResetView) {
                    setResetView(false)
                }
            }
            if (
                (cameraConfig?.target &&
                    !isEqual(
                        cameraConfig?.target,
                        prevProps.cameraConfig?.target,
                    )) ||
                isResetView
            ) {
                viewer.setCameraTargetFromArray({
                    targetArray: cameraConfig?.target || [],
                })
                cameraSet = true

                if (setResetView) {
                    setResetView(false)
                }
            }

            if (
                cameraConfig?.pivot &&
                !isEqual(cameraConfig?.pivot, prevProps.cameraConfig?.pivot)
            ) {
                viewer.setCameraPivotFromArray({
                    pivotArray: cameraConfig?.pivot,
                })
                cameraSet = true
            }

            if (cameraSet) {
                // If we're setting the camera but we're not specifying any specific pivot, let's reset it
                if (!cameraConfig?.pivot) {
                    viewer.setCameraPivotFromArray({ pivotArray: [0, 0, 0] })
                }
                viewer.InteractionController.updateTrackballControls()
            }
        }

        if (this.props.interactionMode !== prevProps.interactionMode) {
            if (this.props.interactionMode === POINT_TO_POINT_MODE) {
                viewer.setPointToPointMeasurementMode({ enable: true })
            } else {
                viewer.InteractionController.pointToPointMeasurementTool.reset()
                viewer.setPointToPointMeasurementMode({ enable: false })
            }
        }

        if (
            prevProps?.customP2PPoints?.length ||
            this.props.customP2PPoints !== prevProps.customP2PPoints
        ) {
            viewer.setCustomMeasurementPoints({
                points: this.props.customP2PPoints || [],
            })
        }

        if (
            prevProps.selectedPoints?.length ||
            this.props.selectedPoints !== prevProps.selectedPoints ||
            this.props.hoveredPoint !== prevProps.hoveredPoint
        ) {
            viewer.setMeasurementPoints({
                selectedPoints: this.props.selectedPoints || [],
                hoveredPoint: this.props.hoveredPoint || null,
            })
        }
        /* Set callbacks for triggering viewer updates in response to events */
        /* e.g. color patches when clicked, remove markup when clicked, etc. */
        const eventCallbackMap = {
            onPatchClick: this.props.onPatchClick || null,
            onPatchCtrlClick: this.props.onPatchCtrlClick || null,
            onPatchRightClick: this.props.onPatchRightClick || null,
            onPatchHover: this.props.onPatchHover || null,
            onCameraPan: this.props.onCameraPan || null,
            onCameraZoom: this.props.onCameraZoom || null,
            onCameraRotation: this.props.onCameraRotation || null,
            onAnnotationClick: this.props.onAnnotationClick || null,
            onCrossSectionToolClick: this.props.onCrossSectionToolClick || null,
            onBackgroundClick: this.props.onBackgroundClick || null,
            onEscKeyUp: this.props.onEscKeyUp || null,
            onCrossSectionToolHover: this.props.onCrossSectionToolHover || null,
            onViewCubeClick: this.props.onViewCubeClick || null,
            onPointHover: this.props.onPointHover || null,
            onPointLeftClick: this.props.onPointLeftClick || null,
            onPointRightClick: this.props.onPointRightClick || null,
            onMultiplePatchHover: this.props.onMultiplePatchHover || null,
            onMultiplePatchClick: this.props.onMultiplePatchClick || null,
        } as { [key: string]: (...args: any[]) => unknown }

        viewer.InteractionController.setSceneEventCallbacks({
            eventCallbackMap,
        })
        viewer.render()
    }

    resetCrossSectionTools() {
        // Temporary, this should be in the 3d-viewer API
        const { viewer } = this

        if (viewer != null) {
            const { crossSectionTools } = viewer.InteractionController

            crossSectionTools.setToolColor({
                toolName: ARROW,
                color: crossSectionTools.arrow.defaultColor as string,
            })
            crossSectionTools.setToolColor({
                toolName: TORUS_X,
                color: crossSectionTools.torusX.defaultColor as string,
            })
            crossSectionTools.setToolColor({
                toolName: TORUS_Y,
                color: crossSectionTools.torusY.defaultColor as string,
            })
        }
    }

    updateCrossSection(
        viewer: ViewerClass,
        crossSectionConfig: CrossSectionConfig,
        prevCrossSectionConfig: Partial<CrossSectionConfig> = {},
        forceUpdate: boolean,
    ) {
        const crossSectionEnabled = crossSectionConfig?.enabled

        this.resetCrossSectionTools()

        viewer.setCrossSectionMode({
            enable: Boolean(crossSectionEnabled),
        })

        if (crossSectionConfig) {
            const {
                crossSectionRotation,
                crossSectionPosition,
                hoveredTool,
                mode,
                toolsHidden,
                view,
            } = crossSectionConfig

            const {
                crossSectionRotation: prevCrossSectionRotation,
                crossSectionPosition: prevCrossSectionPosition,
            } = prevCrossSectionConfig

            if (hoveredTool != null) {
                viewer.InteractionController.crossSectionTools.setToolColor({
                    toolName: hoveredTool,
                    color: HOVERED_CROSS_SECTION_TOOL_COLOR,
                })
            }

            const isCrossSectionDFM =
                crossSectionRotation && crossSectionPosition
            const isPositionRotationChanged =
                crossSectionRotation !== prevCrossSectionRotation &&
                crossSectionPosition !== prevCrossSectionPosition

            if (
                isCrossSectionDFM &&
                (forceUpdate || isPositionRotationChanged)
            ) {
                viewer.InteractionController.crossSectionTools
                    .setToolsPositionFromArray({
                        positionArray: crossSectionPosition,
                    })
                    .setToolsRotation({ rotation: crossSectionRotation })
            }

            if (view) {
                viewer.setDefaultCrossSectionForView(view)
            }

            const crossSectionAnnotationMode =
                mode === CROSS_SECTION_ANNOTATION_MODE

            viewer.setCrossSectionVisibility({
                visible:
                    crossSectionAnnotationMode ||
                    (crossSectionEnabled && !toolsHidden),
            })

            viewer.setCrossSectionInteractions({
                enable: crossSectionEnabled && !toolsHidden,
            })
        }
    }

    renderNoSceneBundle() {
        return <Spin />
    }
    renderUninitializedInitializingState() {
        const { isConfigurationModal } = this.props

        return (
            <Box direction="col">
                <Spin />
                {isConfigurationModal && (
                    <Text fontWeight="normal" style={{ marginTop: '10px' }}>
                        Loading 3D model...
                    </Text>
                )}
            </Box>
        )
    }
    renderFailedToInitializeState() {
        const { isConfigurationModal, openChat } = this.props

        return (
            <>
                {isConfigurationModal ? (
                    <Box direction="col" width="100%">
                        <Box
                            position="absolute"
                            marginTop="48px"
                            justifyContent="center"
                            style={{ top: '10px' }}
                            width="100%"
                        >
                            <Alert
                                className={styles.errorAlert}
                                message={
                                    <Text fontWeight={400}>
                                        There was an error displaying your 3D
                                        model.
                                    </Text>
                                }
                                description={
                                    <Text fontWeight={300}>
                                        Please try to refresh the page. If that
                                        does not resolve the issue, please{' '}
                                        <Link
                                            name="chat-with-us"
                                            onClick={(e: MouseEvent) => {
                                                e.preventDefault()
                                                openChat?.()
                                            }}
                                            placement={PLACEMENT}
                                            data-test-target="chat-with-us"
                                        >
                                            chat with us{' '}
                                        </Link>
                                        for more information.
                                    </Text>
                                }
                                type="error"
                                showIcon
                            />
                        </Box>
                        <Box justifyContent="center">
                            <Image width="400" height="400" src={icon} />
                        </Box>
                    </Box>
                ) : (
                    <Alert
                        message={
                            '3D viewer failed to load. Please message us to help resolve the issue. '
                        }
                        type="error"
                        showIcon
                    />
                )}
            </>
        )
    }
    renderViewerLoadState() {
        switch (this.loadSuccess) {
            case UNINITIALIZED:
                return this.renderUninitializedInitializingState()
            case INITIALIZING:
                return this.renderUninitializedInitializingState()
            case FAILED_TO_INITIALIZE:
                return this.renderFailedToInitializeState()
            case SUCCESSFULLY_INITIALIZED:
                return null
            default:
                return null
        }
    }

    container() {
        const containerStyle: CSSProperties = {
            width:
                this.loadSuccess === SUCCESSFULLY_INITIALIZED
                    ? undefined
                    : '100%',
            height:
                this.loadSuccess === SUCCESSFULLY_INITIALIZED
                    ? undefined
                    : '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }

        return (
            // todo: remove cast to any after PR(https://github.com/fictiv/fictiv-js/pull/5792) is merged
            <Box style={containerStyle as any}>
                {this.props?.sceneBundle
                    ? this.renderViewerLoadState()
                    : this.renderNoSceneBundle()}
            </Box>
        )
    }

    render() {
        return (
            <div className={styles.wrapper}>
                <div className={styles.viewerBackground}>
                    <div
                        ref={this.viewerContainerRef}
                        className={styles.viewerContainer}
                    >
                        {this.container()}
                    </div>
                </div>
            </div>
        )
    }
}

export default sizeMe({
    monitorHeight: true,
    monitorWidth: true,
    monitorPosition: true,
})(ModelViewer)
